/**
 * User preferences (display, notifications, and legacy keys kept for localStorage merge).
 * Persisted in localStorage and used app-wide.
 */
export interface UserPreferences {
  // Display
  theme: 'light' | 'dark' | 'auto';
  fontSize: 'small' | 'medium' | 'large';
  compactMode: boolean;

  // User / tutorial profile
  userType: 'basic' | 'standard' | 'pro' | 'enterprise';
  difficultyLevel: 'beginner' | 'intermediate' | 'advanced';
  preferredLanguage: string;

  // Tutorial behavior
  autoStartTutorials: boolean;
  showProgressNotifications: boolean;
  enableKeyboardShortcuts: boolean;
  tutorialSpeed: number;

  // Content preferences
  showVideoTutorials: boolean;
  showInteractiveTutorials: boolean;
  showFAQ: boolean;
  showTroubleshooting: boolean;

  // Notifications
  emailNotifications: boolean;
  browserNotifications: boolean;
  progressReminders: boolean;
  newTutorialAlerts: boolean;

  // Advanced
  enableAnalytics: boolean;
  enableCrashReporting: boolean;
  enableBetaFeatures: boolean;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'auto',
  fontSize: 'medium',
  compactMode: false,
  userType: 'pro',
  difficultyLevel: 'beginner',
  preferredLanguage: 'en',
  autoStartTutorials: false,
  showProgressNotifications: true,
  enableKeyboardShortcuts: true,
  tutorialSpeed: 1,
  showVideoTutorials: true,
  showInteractiveTutorials: true,
  showFAQ: true,
  showTroubleshooting: true,
  emailNotifications: true,
  browserNotifications: true,
  progressReminders: true,
  newTutorialAlerts: true,
  enableAnalytics: true,
  enableCrashReporting: true,
  enableBetaFeatures: false,
};

const STORAGE_KEY = 'userPreferences';

function loadStored(): UserPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFERENCES };
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return { ...DEFAULT_PREFERENCES, ...parsed };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function loadPreferences(): UserPreferences {
  return loadStored();
}

export function savePreferences(prefs: UserPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Could not save preferences:', e);
  }
}
